import { cn } from '@hbs-us/ui';
import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

/**
 * A small non-modal flyout: a trigger button and an anchored panel that closes on
 * Escape (returning focus to the trigger), on a pointer press outside, and when a
 * link inside is clicked. The launcher and the profile/gear menu both sit on it.
 *
 * Hand-rolled on purpose — the kit ships Radix Dialog/Collapsible only, and the one
 * popover pattern already in the app (the sidebar profile menu) is the same shape.
 * Non-modal by design: the page behind stays live, nothing here needs focus trapping.
 */
export interface FlyoutProps {
  /** Accessible name for the trigger, and for the panel unless `panelLabel` is given. */
  label: string;
  /**
   * A separate name for the PANEL. Needed when the trigger carries visible text: the
   * trigger's accessible name must then contain that text (WCAG 2.5.3, Label in Name),
   * so it can only be the short visible word, which is too thin to name the panel.
   */
  panelLabel?: string;
  /** Trigger contents (an icon); the button itself is rendered here. */
  trigger: ReactNode;
  /** Extra trigger classes; `CHROME_BUTTON_CLASS` is always applied first. */
  triggerClassName?: string;
  children: ReactNode;
  /** Panel width/alignment classes; the base panel styling is always applied. */
  panelClassName?: string;
  /**
   * The trigger's own look. Defaults to `CHROME_BUTTON_CLASS` — the 44px icon button
   * the launcher, the bell and the palette all wear. A trigger that is NOT a chrome
   * icon (the New button is the bar's one primary action) passes its own base here
   * instead, so `triggerClassName` never has to fight a base it did not want.
   *
   * A custom base OWNS ITS OPEN STATE. The chrome button's open/resting tones are
   * `bg-secondary` and `text-muted-foreground`, and applied over a primary-filled
   * trigger they would repaint it grey the moment it opened. Express the open state
   * from `aria-expanded` in `triggerClassName`, the way the avatar's ring does.
   */
  triggerBase?: string;
  /**
   * Open on hover where the pointer can hover, and on tap where it cannot. Off by
   * default: a menu that opens under a passing cursor is right for a create menu
   * you aim at, and wrong for the launcher you pass over on the way to the tabs.
   */
  openOnHover?: boolean;
  /**
   * Dim the page behind the panel below `sm`, where the panel is a bottom sheet.
   * The kit's own dialog overlay, so the sheet and every modal read as one product.
   */
  sheetScrim?: boolean;
  /**
   * Told when the panel opens and closes. The panel's own state stays HERE — a caller
   * that needed to control it would have to reimplement Escape, the outside press and
   * the link-click close. This only reports, so a caller can arm something (a menu's
   * letter keys) for exactly as long as the panel is on screen.
   */
  onOpenChange?: (open: boolean) => void;
}

/** Crossing the gap from the trigger to the panel must not dismiss the panel. */
const HOVER_CLOSE_DELAY_MS = 160;

/**
 * The chrome icon button. ONE definition, worn by the launcher, the gear and the
 * command-palette trigger, so the three cannot drift into three shapes: the kit
 * header's own 44px icon button (mobile menu, panel toggle), which is what the
 * bell already is.
 */
export const CHROME_BUTTON_CLASS =
  'inline-flex size-11 shrink-0 items-center justify-center rounded-md transition-colors ' +
  'duration-[var(--dur-quick)] ease-[var(--ease-out)] motion-reduce:transition-none ' +
  'hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 ' +
  // `ring-offset-card` names the bar these buttons sit on. Left unnamed, Tailwind
  // v4 falls back to `#fff` and the gap renders white in the dark theme.
  'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card';

/**
 * The open state, shown and not merely announced. `aria-expanded` told a screen
 * reader the panel was open while the button looked exactly as it had before, so a
 * keyboard user had no way to see which control owned the panel in front of them.
 * It holds the same tone the bar's own hover uses, so the vocabulary is the bar's.
 */
export const CHROME_BUTTON_OPEN_CLASS = 'bg-secondary text-foreground';
export const CHROME_BUTTON_RESTING_CLASS = 'text-muted-foreground';

export function Flyout({
  label,
  trigger,
  triggerClassName,
  children,
  panelClassName,
  triggerBase,
  openOnHover = false,
  sheetScrim = false,
  onOpenChange,
  panelLabel,
}: FlyoutProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const panelId = useId();

  const cancelScheduledClose = () => {
    if (closeTimer.current !== undefined) window.clearTimeout(closeTimer.current);
    closeTimer.current = undefined;
  };

  // A pending close must not fire into an unmounted component, and it must not
  // survive a route change that took the bar's contents with it. Written inline
  // rather than as a reference to `cancelScheduledClose`, which is rebuilt every
  // render and so would be a dependency this effect must not have.
  useEffect(
    () => () => {
      if (closeTimer.current !== undefined) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  // Report the state; never take it. Deliberately not called during the state update,
  // so a caller re-rendering on it cannot re-enter the setter mid-render.
  const openChanged = useRef(onOpenChange);
  openChanged.current = onOpenChange;
  useEffect(() => {
    openChanged.current?.(open);
  }, [open]);

  /*
   * Read the pointer at the moment of the event rather than holding it in state.
   * `(hover: none)` is the only thing standing between this menu and a touch screen
   * where hover never happens, and the answer can change inside one session — a
   * tablet gains a mouse when its keyboard case is attached. There is nothing to
   * re-render when it does, so a listener would buy nothing.
   */
  // `?? false` is the safe answer, not a shrug: where the query cannot be asked, the
  // menu opens on click alone — which every pointer can do. Assuming hover instead
  // would arm a path a touch screen can never take.
  const pointerCanHover = () => window.matchMedia?.('(hover: hover)').matches ?? false;

  const onPointerEnter = () => {
    if (!openOnHover || !pointerCanHover()) return;
    cancelScheduledClose();
    setOpen(true);
  };

  const onPointerLeave = () => {
    if (!openOnHover || !pointerCanHover()) return;
    cancelScheduledClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target instanceof Node ? event.target : null;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  // Every panel entry is a router Link: a click anywhere in one closes the panel
  // as the navigation starts, so no destination ever opens under its own launcher.
  const onClickCapture = (event: ReactMouseEvent) => {
    if (event.target instanceof Element && event.target.closest('a')) setOpen(false);
  };

  return (
    <div className="relative">
      {/* Below `sm` the panel leaves the bar and becomes a sheet on the bottom edge, so
          the page behind it needs to recede. The scrim is decorative: the document
          pointerdown listener above already closes on a press outside the panel, and a
          press on the scrim is exactly that.

          `--overlay` is the kit's own scrim, the one the mobile nav Drawer already draws,
          and it is a DARK wash in both themes. The command palette's `bg-background/70`
          was the wrong borrow: over a light page it washes the page toward white, and a
          white sheet then has almost nothing to sit against. Measured on this branch at
          390px — the sheet's own border was the only thing separating it from the page. */}
      {open && sheetScrim ? (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-[var(--overlay)] backdrop-blur-sm sm:hidden"
        />
      ) : null}
      <button
        type="button"
        ref={triggerRef}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => {
          // With hover armed on a hovering pointer the panel is ALREADY open by the time
          // a click lands, so a toggle would close what the cursor had just opened — the
          // button punishing the habit of clicking the thing you aimed at. Here a click
          // only opens; Escape, a press outside, and leaving the button all still close.
          if (openOnHover && pointerCanHover()) {
            cancelScheduledClose();
            setOpen(true);
            return;
          }
          setOpen((value) => !value);
        }}
        onMouseEnter={onPointerEnter}
        onMouseLeave={onPointerLeave}
        className={cn(
          triggerBase ?? CHROME_BUTTON_CLASS,
          triggerBase ? null : open ? CHROME_BUTTON_OPEN_CLASS : CHROME_BUTTON_RESTING_CLASS,
          triggerClassName,
        )}
      >
        {trigger}
      </button>
      {open ? (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label={panelLabel ?? label}
          onClickCapture={onClickCapture}
          /* The pair of handlers sits on the BUTTON and on the PANEL rather than on
             the wrapper around both, so the gap between them is covered by the close
             delay instead of by a listening <div> with no role of its own. Leaving the
             button starts the timer; reaching the panel cancels it. */
          onMouseEnter={onPointerEnter}
          onMouseLeave={onPointerLeave}
          className={cn(
            // The bar is 64px and the panel hangs 8px below it. The cap subtracts BOTH
            // — and then some, so the panel ends ABOVE the fold instead of on it.
            // At exactly `100dvh - 4.5rem` the cap equalled bar + gap, so on a 780px
            // phone a full launcher ran to the last pixel of the viewport and cut its
            // final row of labels in half against the screen edge: the panel read as
            // broken rather than as a list with more below. A launcher this tall MUST
            // scroll on a short phone, so the job is to make it look like a scroller —
            // the panel's own border and rounded bottom, visible clear of the fold, is
            // what says the content continues.
            'absolute top-full z-50 mt-2 max-h-[calc(100dvh-6rem)] overflow-y-auto overscroll-contain ' +
              'rounded-lg border border-border bg-popover p-1.5 text-popover-foreground shadow-lg ' +
              // The one authored moment in this chrome: the panel grows out of the
              // corner its own trigger sits in, so the relationship between the button
              // and what it opened is seen rather than inferred. `@starting-style`
              // means the animation is a transition FROM a start frame, not a reveal —
              // where it is unsupported the panel simply appears, fully rendered, and
              // no content has ever depended on it running. 150ms: acknowledgement,
              // not choreography.
              'transition-[opacity,scale] duration-[var(--dur-quick)] ease-[var(--ease-out)] ' +
              'starting:scale-95 starting:opacity-0 motion-reduce:transition-none',
            panelClassName,
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
